import { useState, useEffect, useCallback } from 'react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
}

// Simple event bus for toasts
const listeners: Set<(toasts: Toast[]) => void> = new Set()
let toasts: Toast[] = []

const notify = () => {
  listeners.forEach((listener) => listener([...toasts]))
}

export const toast = {
  show: (message: string, type: ToastType = 'info', duration = 3000) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast = { id, message, type, duration }
    toasts = [...toasts, newToast]
    notify()

    if (duration > 0) {
      setTimeout(() => {
        toast.dismiss(id)
      }, duration)
    }
  },
  success: (message: string, duration?: number) =>
    toast.show(message, 'success', duration),
  error: (message: string, duration?: number) =>
    toast.show(message, 'error', duration),
  info: (message: string, duration?: number) =>
    toast.show(message, 'info', duration),
  warning: (message: string, duration?: number) =>
    toast.show(message, 'warning', duration),
  dismiss: (id: string) => {
    toasts = toasts.filter((t) => t.id !== id)
    notify()
  },
}

export function useToast() {
  const [activeToasts, setActiveToasts] = useState<Toast[]>(toasts)

  useEffect(() => {
    const listener = (newToasts: Toast[]) => {
      setActiveToasts(newToasts)
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  return { toasts: activeToasts, dismiss: toast.dismiss }
}
